/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { compose } from 'recompose';
import { connect, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { bool, object, string } from 'prop-types';
import useAnchorImageToTop from '../../common/useAnchorImageToTop';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';
import ScrollbarContainer from '../../../../GraphqlCategoryPage/Partials/ScrollbarContainer/ScrollbarContainer';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles((theme) => ({
    contentTop: {
        // Desktop
        margin: '0px auto',
        maxWidth: '1380px',
        color: theme.palette.black,
        // Tablet
        [theme.breakpoints.down('md')]: {
            margin: '20px 20px 30px',
        },
        // Mobile
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
        },
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 10px 0',
        },
    },
    contentLeft: {
        width: '100%',
        // Tablet
        [theme.breakpoints.down('md')]: {
            flex: '1 1 auto',
            display: 'flex',
            maxWidth: '100%',
            marginRight: '20px',
            flexDirection: 'column',
        },
        // Mobile
        [theme.breakpoints.down(600)]: {
            maxWidth: '100%',
        },
        marginRight: '24px',
        maxWidth: '665px',
    },
    contentWrapper: {
        maxWidth: '1400px',
        '& $container': {
            marginRight: '16px',
        },
        '& $contentLeft': {
            maxWidth: '752px',
            marginRight: '16px',
            [theme.breakpoints.down('md')]: {
                maxWidth: '100%',
                marginRight: '0px',
            },
        },
        '& $contentRight': {
            maxWidth: '633px',
            padding: '0px 32px',
            [theme.breakpoints.down('md')]: {
                maxWidth: '100%',
                padding: '0px 10px 0px 0px',
            },
            '@media screen and (min-width: 600px) and (max-width:1366px)': {
                maxWidth: '700px',
            },
        },
        '& .stickyImageContainer': {
            '@media screen and (min-width: 600px) and (max-width:1366px)': {
                maxWidth: '48%',
                marginRight: '0px',
            },
            '@media screen and (min-width: 600px) and (max-width:1024px)': {
                marginRight: '16px',
            },
        },
    },
    contentRight: {
        width: '100%',
        maxWidth: '980px',
        // Tablet
        [theme.breakpoints.down('md')]: {
            width: 'calc(60% - 20px)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100%',
        },
    },
    pdpDeliveryImage: {
        display: 'flex',
        flexDirection: 'column',
    },
    pdpShortStyle: {
        '& $contentLeft': {
            marginRight: '0px',
            maxWidth: '670px',
        },
        '& $contentRight': {
            maxWidth: '700px',
            padding: '0px 32px',
            [theme.breakpoints.down('md')]: {
                '@media screen and (min-width: 1024px) and (max-width:1366px)': {
                    padding: '0px 16px 0px 32px',
                    maxWidth: '100%',
                },
                [theme.breakpoints.down('md')]: {
                    padding: '0px 10px 0px 0px',
                },
                '@media screen and (min-width: 600px) and (max-width:700px)': {
                    padding: '0px 6px 0px 8px',
                },
            },
        },
        '& .stickyImageContainer': {
            maxWidth: '670px',
            '@media screen and (min-width: 1025px) and (max-width:1366px)': {
                maxWidth: '46%',
                marginRight: '0px',
            },
            '@media screen and (min-width: 600px) and (max-width:1024px)': {
                maxWidth: '40%',
                marginRight: '0px',
            },
        },
    },
}));

// documentation: https://1800flowersinc.atlassian.net/wiki/spaces/RP/pages/288227337/PDP+Product+Details+Page
const TwoColumn = (props) => {
    const {
        ssrDeviceType,
        components,
        isPdpRedesignEnabled,
        showProductTitleTop,
        isPdpRedesignShortEnabled,
        isPassportAfterCta,
        isPdpPassportHidden,
        stickyAtcEnabled,
    } = props;

    const {
        ProductImageUI,
        SmartGift,
        ProductTitle,
        ProductReview,
        GraphqlProductSkus,
        GraphqlAddonContainer,
        GCIPersonalization,
        SnipeBanner,
        GraphqlAgeVerification,
        SubscriptionProductBannerUI,
        ProductQuantity,
        GraphqlPDPPromoContainerTop,
        GraphqlPDPPromoContainer,
        PmallSummary,
        PmallPersonalization,
        PassportBannerUI,
        PDPAddToCart,
        GoDeluxe,
        NBCUMovieBanner,
        PDPDeliveryPopupContainer,
        SubstitutionMessage,
        PromoMessageUI,
        PLAProductIcon,
        PassportEligibleImage,
        ProductDetails,
        QandASection,
    } = components;

    const classes = useStyles();

    const isPdpDeliveryTopEnabled = useSelector(getFeatureFlag('is-pdp-delivery-top-enabled'));
    const showDeliveryField = !(showProductTitleTop && isPdpDeliveryTopEnabled);
    const idFullStory = isPdpRedesignShortEnabled ? 'pdp-short-design' : 'pdp-long-design';
    // For ProductReview
    const isPdpReviewsEnabled = useSelector(getFeatureFlag('is-pdp-reviews-enabled'));
    const isPowerReviewEnabled = useSelector(getFeatureFlag('is-power-review-enabled'));

    // uncomment when needed
    // const variantName = 'TwoColumn'; // this should be unique and never change
    let pdpReskinStyle = '';
    if (isPdpRedesignShortEnabled) {
        pdpReskinStyle = `${classes.contentWrapper} ${classes.pdpShortStyle}`;
    } else if (isPdpRedesignEnabled) {
        pdpReskinStyle = classes.contentWrapper;
    }
    useAnchorImageToTop({
        imageElementId: 'productImage',
        anchorOffset: 16,
        shouldAnchor: stickyAtcEnabled,
        isMobileDevice: ssrDeviceType === 'mobile',
    });

    const desktopLayout = () => (
        <div
            className={`${classes.contentTop} ${pdpReskinStyle}`}
            data-testid="pdp-variant-twocolumn"
            id={idFullStory}
        >
            <ScrollbarContainer
                contentContainerId="twoColumnRight"
                isFoodBrand
                type="productBase"
            >
                <div className={classes.contentLeft}>
                    {ProductImageUI()}
                    {!isPdpRedesignEnabled && SubstitutionMessage()}
                    {SmartGift()}
                </div>
            </ScrollbarContainer>
            <div className={classes.contentRight} id="twoColumnRight">
                {ProductTitle()}
                <div>
                    {isPdpRedesignEnabled && PassportEligibleImage()}
                    {isPdpRedesignEnabled && PDPDeliveryPopupContainer()}
                </div>
                {isPdpRedesignEnabled && SubstitutionMessage()}
                {isPdpReviewsEnabled && isPowerReviewEnabled && ProductReview()}
                {GraphqlPDPPromoContainerTop()}
                {GraphqlProductSkus()}
                {GraphqlAddonContainer()}
                {GCIPersonalization()}
                {SnipeBanner()}
                {GraphqlAgeVerification()}
                {SubscriptionProductBannerUI()}
                {PmallSummary()}
                {GraphqlPDPPromoContainer()}
                {PmallPersonalization()}
                {!isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                {ProductQuantity()}
                {PDPAddToCart()}
                {isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                {NBCUMovieBanner()}
                {isPdpRedesignEnabled && !isPdpRedesignShortEnabled && ProductDetails()}
                {!isPdpRedesignEnabled && PDPDeliveryPopupContainer()}
                {PromoMessageUI()}
                {QandASection()}
            </div>
        </div>
    );

    const mobileLayout = () => (
        <>
            {PLAProductIcon()}
            <div className={classes.contentTop} id={idFullStory}>
                <div className={classes.contentLeft}>
                    {showProductTitleTop && ProductTitle()}
                    {showProductTitleTop && (
                        <div className={classes.pdpDeliveryImage}>
                            {PassportEligibleImage()}
                            {PDPDeliveryPopupContainer()}
                        </div>
                    )}
                    {showProductTitleTop && isPdpDeliveryTopEnabled && PDPAddToCart('twoColumn', false)}
                    <div id="productImage">{ProductImageUI()}</div>
                </div>
                <div className={classes.contentRight}>
                    {!showProductTitleTop && ProductTitle()}
                    {isPdpReviewsEnabled && isPowerReviewEnabled && ProductReview()}
                    {isPdpRedesignEnabled && !showProductTitleTop && PassportEligibleImage()}
                    {isPdpRedesignEnabled && !showProductTitleTop && PDPDeliveryPopupContainer()}
                    {isPdpRedesignEnabled && SubstitutionMessage()}
                    {GraphqlPDPPromoContainerTop()}
                    {GraphqlProductSkus()}
                    {GoDeluxe(null, true)}
                    {GraphqlAddonContainer()}
                    {GCIPersonalization()}
                    {SnipeBanner()}
                    {GraphqlAgeVerification()}
                    {SubscriptionProductBannerUI()}
                    {PmallSummary()}
                    {GraphqlPDPPromoContainer()}
                    {PmallPersonalization()}
                    {!isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                    {ProductQuantity()}
                    {PDPAddToCart('twoColumn', true, showDeliveryField)}
                    {isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                    {isPdpRedesignEnabled && !isPdpRedesignShortEnabled && ProductDetails()}
                    {NBCUMovieBanner()}
                    {!isPdpRedesignEnabled && PDPDeliveryPopupContainer()}
                    {PromoMessageUI()}
                    {SmartGift()}
                    {!isPdpRedesignEnabled && SubstitutionMessage()}
                    {GoDeluxe()}
                    {QandASection()}
                </div>
            </div>
        </>
    );

    if (ssrDeviceType === 'mobile') {
        return mobileLayout();
    }

    return desktopLayout();
};

const mapStateToProps = (state) => ({
    ssrDeviceType: getSSRDeviceType(state),
});

TwoColumn.propTypes = {
    ssrDeviceType: string.isRequired,
    components: object.isRequired,
    isPdpRedesignEnabled: bool,
    isPdpRedesignShortEnabled: bool,
    showProductTitleTop: bool,
    isPassportAfterCta: bool,
    isPdpPassportHidden: bool,
    stickyAtcEnabled: bool,
};
TwoColumn.defaultProps = {
    isPdpRedesignEnabled: false,
    isPdpRedesignShortEnabled: false,
    showProductTitleTop: false,
    isPassportAfterCta: false,
    isPdpPassportHidden: false,
    stickyAtcEnabled: false,
};

const enhance = compose(
    connect(mapStateToProps),
);

export default enhance(TwoColumn);
